// src/components/EditPanels/HouseholdEditPanel.js
import React from 'react';
import { Trash2 } from 'lucide-react';
import { useGenogram } from '../../contexts/GenogramContext';

const HouseholdEditPanel = () => {
  const { state, actions } = useGenogram();
  const { selectedHousehold } = state;

  if (!selectedHousehold) return null;

  const updateHousehold = (updates) => {
    actions.updateHousehold(selectedHousehold.id, updates);
    
    // Save to history after household property updates
    actions.saveToHistory({
      people: state.people,
      relationships: state.relationships,
      households: state.households,
      textBoxes: state.textBoxes
    });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      {/* Household Name */}
      <div>
        <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '8px' }}>
          Household Name
        </label>
        <input
          type="text"
          value={selectedHousehold.name}
          onChange={(e) => updateHousehold({ name: e.target.value })}
          style={{
            width: '100%',
            border: '1px solid #d1d5db',
            borderRadius: '12px',
            padding: '12px 16px',
            fontSize: '14px',
            backgroundColor: '#f9fafb'
          }}
        />
      </div>

      {/* Border Color */}
      <div>
        <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '8px' }}>
          Border Color
        </label>
        <input
          type="color"
          value={selectedHousehold.color || '#6366f1'}
          onChange={(e) => updateHousehold({ color: e.target.value })}
          style={{
            width: '100%',
            height: '48px',
            border: '1px solid #d1d5db',
            borderRadius: '12px',
            padding: '8px',
            backgroundColor: '#f9fafb',
            cursor: 'pointer'
          }}
        />
      </div>

      {/* Border Smoothness */}
      <div>
        <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '8px' }}>
          Border Smoothness
        </label>
        <div style={{ marginBottom: '8px' }}>
          <input
            type="range"
            min="0"
            max="0.5"
            step="0.05"
            value={selectedHousehold.smoothness || 0.25}
            onChange={(e) => updateHousehold({ smoothness: parseFloat(e.target.value) })}
            style={{
              width: '100%',
              height: '6px',
              borderRadius: '3px',
              background: `linear-gradient(to right, #e5e7eb 0%, #e5e7eb ${((selectedHousehold.smoothness || 0.25) / 0.5) * 100}%, #6366f1 ${((selectedHousehold.smoothness || 0.25) / 0.5) * 100}%, #6366f1 100%)`,
              outline: 'none',
              cursor: 'pointer'
            }}
          />
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#6b7280' }}>
          <span>Sharp corners</span>
          <span style={{ color: '#6366f1', fontWeight: '500' }}>
            {Math.round(((selectedHousehold.smoothness || 0.25) / 0.5) * 100)}% smooth
          </span>
          <span>Very curved</span>
        </div>
      </div>

      {/* Instructions */}
      <div>
        <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '8px' }}>
          Instructions
        </label>
        <div style={{
          padding: '12px 16px',
          backgroundColor: '#f9fafb',
          borderRadius: '12px',
          border: '1px solid #d1d5db',
          fontSize: '13px',
          color: '#6b7280'
        }}>
          <p style={{ margin: '0 0 8px 0' }}>• Click and drag the boundary to move it</p>
          <p style={{ margin: '0 0 8px 0' }}>• Click and drag points to reshape</p>
          <p style={{ margin: '0 0 8px 0' }}>• Right-click boundary to add points</p>
          <p style={{ margin: '0 0 8px 0' }}>• Right-click points to delete them</p>
          <p style={{ margin: '0' }}>• Click the × button to delete</p>
        </div>
      </div>

      {/* Notes */}
      <div>
        <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '8px' }}>
          Notes
        </label>
        <textarea
          value={selectedHousehold.notes || ''}
          onChange={(e) => updateHousehold({ notes: e.target.value })}
          placeholder="Add notes about this household..."
          style={{
            width: '100%',
            height: '80px',
            border: '1px solid #d1d5db',
            borderRadius: '12px',
            padding: '12px 16px',
            fontSize: '14px',
            backgroundColor: '#f9fafb',
            resize: 'vertical'
          }}
        />
      </div>

      {/* Delete Button */}
      <button
        onClick={() => {
          actions.setDeleteConfirmation({
            type: 'household',
            title: 'Delete Household',
            message: `Delete ${selectedHousehold.name}?`,
            onConfirm: () => {
              actions.deleteHousehold(selectedHousehold.id);
              actions.setDeleteConfirmation(null);
              
              // Save to history after household deletion
              actions.saveToHistory({
                people: state.people,
                relationships: state.relationships,
                households: state.households.filter(h => h.id !== selectedHousehold.id),
                textBoxes: state.textBoxes
              });
            },
            onCancel: () => actions.setDeleteConfirmation(null)
          });
        }}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
          backgroundColor: '#fee2e2',
          color: '#dc2626',
          padding: '12px 16px',
          borderRadius: '12px',
          border: '1px solid #fecaca',
          cursor: 'pointer',
          fontSize: '14px',
          fontWeight: '500'
        }}
      >
        <Trash2 size={16} />
        Delete Household
      </button>
    </div>
  );
};

export default HouseholdEditPanel;